import { useLocalStorage } from "@hooks/useLocalStorage";
import { jwtDecode } from "jwt-decode";

export type UserInfo = {
	id: string;
	name: string | undefined;
	firstName: string | undefined;
	lastName: string | undefined;
	role: "baceadmin" | "user";
	organization:
		| {
				id: string;
				role: "owner" | "admin" | "member";
		  }
		| undefined;
	avatar?: string;
	email: string;
	verified: boolean;
};

export const useUserInfo = (): UserInfo | undefined => {
	const [token] = useLocalStorage({ key: "better-auth-jwt", json: false });
	if (!token) return undefined;
	const { sub, claims } = jwtDecode<{ sub: string; claims: any }>(token);
	const isAnonAppleUser = claims.name.endsWith("@privaterelay.appleid.com");
	return {
		id: sub,
		...claims,
		name: isAnonAppleUser ? undefined : claims.name,
		firstName: isAnonAppleUser ? undefined : claims.firstName,
		lastName: isAnonAppleUser ? undefined : claims.lastName,
	};
};

/**
 * Synchronously reads the current user's role from the stored JWT.
 * Usable outside React (e.g. route loaders) where hooks are unavailable.
 * Returns `undefined` when there is no valid session.
 */
export const getUserRole = (): UserInfo["role"] | undefined => {
	const token = localStorage.getItem("better-auth-jwt");
	if (!token) return undefined;
	try {
		const { claims } = jwtDecode<{ claims: { role?: UserInfo["role"] } }>(token);
		return claims.role;
	} catch {
		return undefined;
	}
};

/** Synchronously reads the current user's id from the stored JWT (usable in route loaders). */
export const getUserId = (): string | undefined => {
	const token = localStorage.getItem("better-auth-jwt");
	if (!token) return undefined;
	try {
		return jwtDecode<{ sub?: string }>(token).sub;
	} catch {
		return undefined;
	}
};

export const getUserOrganizationId = (): string | undefined => {
	const token = localStorage.getItem("better-auth-jwt");
	if (!token) return undefined;
	try {
		const { claims } = jwtDecode<{ claims: { organization?: UserInfo["organization"] } }>(token);
		return claims.organization?.id;
	} catch {
		return undefined;
	}
};

/** Whether the current session belongs to a baceadmin (platform-level admin). */
export const isBaceAdmin = (): boolean => getUserRole() === "baceadmin";
